import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, AlignLeft, List, Package } from 'lucide-react';

const ParameterBuilder = ({ value, onChange }) => {
  const [parameters, setParameters] = useState([]);
  const [showAddParam, setShowAddParam] = useState(false);
  const [expandedParams, setExpandedParams] = useState({});

  // Tipos de dados disponíveis
  const dataTypes = [
    { value: 'string', label: 'Texto (string)', icon: <AlignLeft className="w-4 h-4 text-blue-600" /> },
    { value: 'number', label: 'Número (number)', icon: <span className="w-4 h-4 text-green-600 font-mono">123</span> },
    { value: 'boolean', label: 'Boolean', icon: <span className="w-4 h-4 text-green-600 font-mono">T/F</span> },
    { value: 'array', label: 'Lista (array)', icon: <List className="w-4 h-4 text-orange-600" /> },
    { value: 'object', label: 'Objeto', icon: <Package className="w-4 h-4 text-green-600" /> }
  ];

  // Carregar parâmetros do valor inicial (se existir)
  useEffect(() => {
    if (value) {
      try {
        // Se o valor for uma string, tentar analisá-lo como JSON
        if (typeof value === 'string') {
          const parsedValue = JSON.parse(value);
          if (parsedValue.properties) {
            // Converter o esquema JSON em nosso formato interno
            const parsedParams = Object.entries(parsedValue.properties).map(([name, prop]) => {
              let param = {
                name,
                type: prop.type,
                description: prop.description || '',
                required: parsedValue.required?.includes(name) || false
              };
              
              // Se for um array, verificar se tem items definidos
              if (prop.type === 'array' && prop.items) {
                param.items = {
                  type: prop.items.type || 'string',
                  description: prop.items.description || ''
                };
                
                // Se os items forem objetos, processar suas propriedades
                if (prop.items.type === 'object' && prop.items.properties) {
                  param.items.properties = Object.entries(prop.items.properties).map(([propName, propDetails]) => ({
                    name: propName,
                    type: propDetails.type || 'string',
                    description: propDetails.description || '',
                    required: prop.items.required?.includes(propName) || false
                  }));
                }
              }
              
              // Se for um objeto, processar suas propriedades
              if (prop.type === 'object' && prop.properties) {
                param.properties = Object.entries(prop.properties).map(([propName, propDetails]) => ({
                  name: propName,
                  type: propDetails.type || 'string',
                  description: propDetails.description || '',
                  required: prop.required?.includes(propName) || false
                }));
              }
              
              return param;
            });
            
            setParameters(parsedParams);
          }
        } else if (typeof value === 'object' && value !== null) {
          // O valor já está no formato esperado
          setParameters(value);
        }
      } catch (e) {
        console.error("Erro ao analisar valor do builder de parâmetros:", e);
        setParameters([]);
      }
    } else {
      setParameters([]);
    }
  }, []);

  // Atualizar o valor do campo quando os parâmetros mudarem
  useEffect(() => {
    // Converter nosso formato interno para o esquema JSON esperado pelo API
    const schema = {
      type: 'object',
      properties: {},
      required: []
    };
    
    parameters.forEach(param => {
      schema.properties[param.name] = {
        type: param.type,
        description: param.description || ''
      };
      
      if (param.required) {
        schema.required.push(param.name);
      }
      
      // Processar propriedades para arrays
      if (param.type === 'array') {
        // Garantir que o campo items sempre existe
        if (!param.items) {
          param.items = { type: 'string' };
        }
        
        schema.properties[param.name].items = {
          type: param.items.type || 'string'
        };
        
        if (param.items.description) {
          schema.properties[param.name].items.description = param.items.description;
        }
        
        // Se os itens forem objetos, adicionar suas propriedades
        if (param.items.type === 'object' && param.items.properties && param.items.properties.length > 0) {
          schema.properties[param.name].items.properties = {};
          schema.properties[param.name].items.required = [];
          // Adicionar additionalProperties: false para objetos
          schema.properties[param.name].items.additionalProperties = false;
          
          param.items.properties.forEach(prop => {
            schema.properties[param.name].items.properties[prop.name] = {
              type: prop.type,
              description: prop.description || ''
            };
            
            if (prop.required) {
              schema.properties[param.name].items.required.push(prop.name);
            }
          });
          
          // Remover array required se estiver vazio
          if (schema.properties[param.name].items.required.length === 0) {
            delete schema.properties[param.name].items.required;
          }
        }
      }
      
      // Processar propriedades para objetos
      if (param.type === 'object' && param.properties && param.properties.length > 0) {
        schema.properties[param.name].properties = {};
        schema.properties[param.name].required = [];
        // Adicionar additionalProperties: false para objetos
        schema.properties[param.name].additionalProperties = false;
        
        param.properties.forEach(prop => {
          schema.properties[param.name].properties[prop.name] = {
            type: prop.type,
            description: prop.description || ''
          };
          
          if (prop.required) {
            schema.properties[param.name].required.push(prop.name);
          }
        });
        
        // Remover array required se estiver vazio
        if (schema.properties[param.name].required.length === 0) {
          delete schema.properties[param.name].required;
        }
      }
    });
    
    // Remover array required se estiver vazio
    if (schema.required.length === 0) {
      delete schema.required;
    }
    
    // Converter para string JSON para armazenar
    onChange(JSON.stringify(schema, null, 2));
  }, [parameters]);

  // Adicionar novo parâmetro
  const addParameter = (param) => {
    setParameters([...parameters, param]);
    setShowAddParam(false);
  };

  // Remover parâmetro
  const removeParameter = (index) => {
    const updatedParams = [...parameters];
    updatedParams.splice(index, 1);
    setParameters(updatedParams);
  };

  // Atualizar parâmetro existente
  const updateParameter = (index, field, value) => {
    const updatedParams = [...parameters];
    updatedParams[index] = { ...updatedParams[index], [field]: value };
    setParameters(updatedParams);
  };

  // Toggle expansão de um parâmetro complexo
  const toggleParamExpansion = (index) => {
    setExpandedParams(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Adicionar propriedade a um objeto
  const addObjectProperty = (paramIndex, property) => {
    const updatedParams = [...parameters];
    if (!updatedParams[paramIndex].properties) {
      updatedParams[paramIndex].properties = [];
    }
    updatedParams[paramIndex].properties.push(property);
    setParameters(updatedParams);
  };

  // Remover propriedade de um objeto
  const removeObjectProperty = (paramIndex, propIndex) => {
    const updatedParams = [...parameters];
    updatedParams[paramIndex].properties.splice(propIndex, 1);
    setParameters(updatedParams);
  };

  // Atualizar propriedade de um objeto
  const updateObjectProperty = (paramIndex, propIndex, field, value) => {
    const updatedParams = [...parameters];
    updatedParams[paramIndex].properties[propIndex] = {
      ...updatedParams[paramIndex].properties[propIndex],
      [field]: value
    };
    setParameters(updatedParams);
  };

  // Adicionar propriedade a um array de objetos
  const addArrayItemProperty = (paramIndex, property) => {
    const updatedParams = [...parameters];
    if (!updatedParams[paramIndex].items) {
      updatedParams[paramIndex].items = { type: 'object', properties: [] };
    }
    if (!updatedParams[paramIndex].items.properties) {
      updatedParams[paramIndex].items.properties = [];
    }
    updatedParams[paramIndex].items.properties.push(property);
    setParameters(updatedParams);
  };

  // Remover propriedade de um array de objetos
  const removeArrayItemProperty = (paramIndex, propIndex) => {
    const updatedParams = [...parameters];
    updatedParams[paramIndex].items.properties.splice(propIndex, 1);
    setParameters(updatedParams);
  };

  // Atualizar propriedade de um array de objetos
  const updateArrayItemProperty = (paramIndex, propIndex, field, value) => {
    const updatedParams = [...parameters];
    updatedParams[paramIndex].items.properties[propIndex] = {
      ...updatedParams[paramIndex].items.properties[propIndex],
      [field]: value
    };
    setParameters(updatedParams);
  };

  // Atualizar o tipo de itens de um array
  const updateArrayItemType = (paramIndex, type) => {
    const updatedParams = [...parameters];
    if (!updatedParams[paramIndex].items) {
      updatedParams[paramIndex].items = { type };
    } else {
      updatedParams[paramIndex].items.type = type;
      
      // Se mudar de objeto para outro tipo, remover propriedades
      if (type !== 'object') {
        delete updatedParams[paramIndex].items.properties;
      } else if (!updatedParams[paramIndex].items.properties) {
        updatedParams[paramIndex].items.properties = [];
      }
    }
    setParameters(updatedParams);
  };

  // Atualizar a descrição dos itens de um array
  const updateArrayItemDescription = (paramIndex, description) => {
    const updatedParams = [...parameters];
    if (!updatedParams[paramIndex].items) {
      updatedParams[paramIndex].items = { type: 'string', description };
    } else {
      updatedParams[paramIndex].items.description = description;
    }
    setParameters(updatedParams);
  };

  // Formulário para novo parâmetro
  const NewParameterForm = () => {
    const [newParam, setNewParam] = useState({
      name: '',
      type: 'string',
      description: '',
      required: false
    });

    const handleChange = (field, value) => {
      setNewParam({ ...newParam, [field]: value });
    };

    const handleSubmit = () => {
      // Validar nome do parâmetro
      if (!newParam.name.trim()) {
        alert('O nome do parâmetro é obrigatório');
        return;
      }
      
      // Verificar se já existe um parâmetro com este nome
      if (parameters.some(p => p.name === newParam.name)) {
        alert('Já existe um parâmetro com este nome');
        return;
      }

      // Adicionar propriedades específicas por tipo
      const paramToAdd = { ...newParam };
      
      if (newParam.type === 'array') {
        paramToAdd.items = { type: 'string' };
      } else if (newParam.type === 'object') {
        paramToAdd.properties = [];
      }

      addParameter(paramToAdd);
      setNewParam({ name: '', type: 'string', description: '', required: false });
    };

    return (
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Adicionar Novo Parâmetro</h4>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nome</label>
            <input
              type="text"
              value={newParam.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
              placeholder="nome_do_parametro"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={newParam.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
            >
              {dataTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              value={newParam.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
              rows="2"
              placeholder="Descrição que aparecerá para o agente"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="param-required"
              checked={newParam.required}
              onChange={(e) => handleChange('required', e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded"
            />
            <label htmlFor="param-required" className="ml-2 block text-sm text-gray-700">
              Obrigatório
            </label>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            onClick={() => setShowAddParam(false)}
            className="px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Adicionar
          </button>
        </div>
      </div>
    );
  };

  // Formulário para nova propriedade de objeto
  const NewObjectPropertyForm = ({ paramIndex, onAdd, onCancel }) => {
    const [newProp, setNewProp] = useState({
      name: '',
      type: 'string',
      description: '',
      required: false
    });

    const handleChange = (field, value) => {
      setNewProp({ ...newProp, [field]: value });
    };

    const handleSubmit = () => {
      // Validar nome da propriedade
      if (!newProp.name.trim()) {
        alert('O nome da propriedade é obrigatório');
        return;
      }
      
      // Verificar se já existe uma propriedade com este nome
      const existingProps = parameters[paramIndex].properties || [];
      if (existingProps.some(p => p.name === newProp.name)) {
        alert('Já existe uma propriedade com este nome');
        return;
      }

      onAdd(newProp);
      setNewProp({ name: '', type: 'string', description: '', required: false });
      onCancel();
    };

    return (
      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3 ml-4">
        <h5 className="text-xs font-medium text-gray-700 mb-2">Adicionar Propriedade</h5>
        
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nome</label>
            <input
              type="text"
              value={newProp.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md"
              placeholder="nome_da_propriedade"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={newProp.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md"
            >
              {dataTypes.slice(0, 3).map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
            <input
              type="text"
              value={newProp.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md"
              placeholder="Descrição que aparecerá para o agente"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`prop-required-${paramIndex}`}
              checked={newProp.required}
              onChange={(e) => handleChange('required', e.target.checked)}
              className="h-3 w-3 text-blue-600 rounded"
            />
            <label htmlFor={`prop-required-${paramIndex}`} className="ml-2 block text-xs text-gray-700">
              Obrigatório
            </label>
          </div>
        </div>
        
        <div className="mt-3 flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-2 py-1 text-xs text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-2 py-1 text-xs text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Adicionar
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-300 flex justify-between items-center">
        <h3 className="font-medium text-gray-700 text-sm">Construtor de Parâmetros</h3>
        <button
          type="button"
          onClick={() => setShowAddParam(true)}
          className="px-2 py-1 text-xs flex items-center bg-blue-600 text-white rounded hover:bg-blue-700"
          disabled={showAddParam}
        >
          <Plus className="w-3 h-3 mr-1" />
          Adicionar Parâmetro
        </button>
      </div>
      
      <div className="p-4 bg-white space-y-4">
        {/* Formulário para novo parâmetro */}
        {showAddParam && <NewParameterForm />}
        
        {/* Lista de parâmetros */}
        {parameters.length === 0 ? (
          <div className="text-center py-6 text-sm text-gray-500">
            Nenhum parâmetro definido. Clique em "Adicionar Parâmetro" para começar.
          </div>
        ) : (
          <div className="space-y-3">
            {parameters.map((param, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border-b">
                  <div className="flex items-center">
                    {(param.type === 'object' || param.type === 'array') && (
                      <button
                        type="button"
                        className="mr-1 p-1 hover:bg-gray-200 rounded"
                        onClick={() => toggleParamExpansion(index)}
                      >
                        {expandedParams[index] ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    )}
                    
                    {dataTypes.find(t => t.value === param.type)?.icon}
                    
                    <span className="ml-2 font-medium text-sm">{param.name}</span>
                    <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                      {param.type}
                    </span>
                    {param.required && (
                      <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                        Obrigatório
                      </span>
                    )}
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => removeParameter(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="p-3 space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
                    <textarea
                      value={param.description || ''}
                      onChange={(e) => updateParameter(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                      rows="2"
                      placeholder="Descrição para o agente sobre este parâmetro"
                    />
                  </div>
                  
                  {/* Configurações específicas para arrays */}
                  {param.type === 'array' && expandedParams[index] && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Configurações do Array</h4>
                      
                      <div className="space-y-2 ml-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Tipo dos Itens</label>
                          <select
                            value={param.items?.type || 'string'}
                            onChange={(e) => updateArrayItemType(index, e.target.value)}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md"
                          >
                            {dataTypes.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        {param.items?.type !== 'object' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Descrição dos Itens</label>
                            <input
                              type="text"
                              value={param.items?.description || ''}
                              onChange={(e) => updateArrayItemDescription(index, e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md"
                              placeholder="Descrição dos itens do array"
                            />
                          </div>
                        )}
                        
                        {/* Configurações para arrays de objetos */}
                        {param.items?.type === 'object' && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-xs font-medium text-gray-700">Propriedades dos Objetos</h5>
                              
                              {/* Botão para adicionar propriedade ao objeto */}
                              <button
                                type="button"
                                onClick={() => setExpandedParams(prev => ({
                                  ...prev,
                                  [`${index}-newArrayProp`]: true
                                }))}
                                className="text-xs flex items-center text-blue-600 hover:text-blue-800"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Adicionar Propriedade
                              </button>
                            </div>
                            
                            {/* Formulário para nova propriedade de array */}
                            {expandedParams[`${index}-newArrayProp`] && (
                              <NewObjectPropertyForm
                                paramIndex={index}
                                onAdd={(prop) => addArrayItemProperty(index, prop)}
                                onCancel={() => setExpandedParams(prev => ({
                                  ...prev,
                                  [`${index}-newArrayProp`]: false
                                }))}
                              />
                            )}
                            
                            {/* Lista de propriedades do objeto */}
                            {param.items?.properties?.length > 0 ? (
                              <div className="space-y-2 ml-2">
                                {param.items.properties.map((prop, propIndex) => (
                                  <div key={propIndex} className="border border-gray-200 rounded p-2 bg-gray-50 flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center">
                                        {dataTypes.find(t => t.value === prop.type)?.icon}
                                        <span className="ml-1 font-medium text-xs">{prop.name}</span>
                                        <span className="ml-1 text-xs bg-gray-200 text-gray-700 px-1 py-0.5 rounded">
                                          {prop.type}
                                        </span>
                                        {prop.required && (
                                          <span className="ml-1 text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded">
                                            Req
                                          </span>
                                        )}
                                      </div>
                                      <div className="mt-1 text-xs text-gray-600">
                                        {prop.description || <span className="italic">Sem descrição</span>}
                                      </div>
                                    </div>
                                    
                                    <button
                                      type="button"
                                      onClick={() => removeArrayItemProperty(index, propIndex)}
                                      className="text-red-500 hover:text-red-700 p-1"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 italic ml-2">
                                Nenhuma propriedade definida
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Configurações específicas para objetos */}
                  {param.type === 'object' && expandedParams[index] && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-medium text-gray-700">Propriedades do Objeto</h4>
                        
                        {/* Botão para adicionar propriedade ao objeto */}
                        <button
                          type="button"
                          onClick={() => setExpandedParams(prev => ({
                            ...prev,
                            [`${index}-newProp`]: true
                          }))}
                          className="text-xs flex items-center text-blue-600 hover:text-blue-800"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Adicionar Propriedade
                        </button>
                      </div>
                      
                      {/* Formulário para nova propriedade */}
                      {expandedParams[`${index}-newProp`] && (
                        <NewObjectPropertyForm
                          paramIndex={index}
                          onAdd={(prop) => addObjectProperty(index, prop)}
                          onCancel={() => setExpandedParams(prev => ({
                            ...prev,
                            [`${index}-newProp`]: false
                          }))}
                        />
                      )}
                      
                      {/* Lista de propriedades do objeto */}
                      {param.properties?.length > 0 ? (
                        <div className="space-y-2 ml-2">
                          {param.properties.map((prop, propIndex) => (
                            <div key={propIndex} className="border border-gray-200 rounded p-2 bg-gray-50 flex items-center justify-between">
                              <div>
                                <div className="flex items-center">
                                  {dataTypes.find(t => t.value === prop.type)?.icon}
                                  <span className="ml-1 font-medium text-xs">{prop.name}</span>
                                  <span className="ml-1 text-xs bg-gray-200 text-gray-700 px-1 py-0.5 rounded">
                                    {prop.type}
                                  </span>
                                  {prop.required && (
                                    <span className="ml-1 text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded">
                                      Req
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 text-xs text-gray-600">
                                  {prop.description || <span className="italic">Sem descrição</span>}
                                </div>
                              </div>
                              
                              <button
                                type="button"
                                onClick={() => removeObjectProperty(index, propIndex)}
                                className="text-red-500 hover:text-red-700 p-1"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 italic ml-2">
                          Nenhuma propriedade definida
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParameterBuilder;